import { PartOrder } from 'app/models/PartOrder';
import Modal from 'react-responsive-modal';
import React, { useEffect, useState } from 'react';
import TitleModal from 'app/components/Modal/TitleModal';
import { ProductOrder } from 'app/models/ProductOrder';
import { PackageOrder } from 'app/models/PackageOrder';
import { Checkbox } from '@mui/material';
import Img from 'app/components/Img';
import _ from 'lodash';
import {
  createProductionPiece,
  ProductionPiece,
  ProductionPieceJson,
} from 'app/models/ProductionPiece';
import Button from 'app/components/Button';
import { FiPrinter } from 'react-icons/fi';
import { GiCircularSawblade } from 'react-icons/gi';
import { FaWineGlass, RiArchiveDrawerLine } from 'react-icons/all';
import { Workstation } from 'app/containers/OrderDetails/types';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectDirectPartOrderProductsFiltered,
  selectHasCuttingPiece,
} from 'app/containers/OrderDetails/PartOrderSelection/selectors';
import PrintOrder from 'app/containers/OrderDetails/HeaderOrderDetails/PrintOrder';
import { PARTORDER_URL } from 'app/containers/SiteWrapper/constants';
import { requestPost } from 'utils/requests';
import {
  getAllPartOrders,
  getPartOrder,
} from 'app/containers/SiteWrapper/slice';
import { DESTINATION_LEUZE_ID } from 'app/constants';
import {
  selectOrderLoading,
  selectOrdersMap,
} from 'app/containers/SiteWrapper/selectors';
import { CircleSpinner } from 'react-spinners-kit';
import { CUTTING_PRINTING_INFO_URL } from 'app/containers/Cutting/constants';
import { Color, findColor } from 'app/models/Color';
import { CuttingToPrint } from 'app/containers/Cutting/CuttingToPrint';
import { selectAllColors } from 'store/slices/colorSlice';
import { useSubdividePartOrderMutation } from 'services/kewloxApi';

interface Props {
  partOrder: PartOrder;
  setIsPrintDirectPartOrderModal: any;
  isPrintDirectPartOrderModalOpen: boolean;
}

export function PrintDirectPartOrderModal({
  partOrder,
  isPrintDirectPartOrderModalOpen,
  setIsPrintDirectPartOrderModal,
}: Props) {
  const [selectedPieces, setSelectedPieces] = useState<number[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [filtered, setFiltered] = useState<boolean>(true);
  const [selectionMode, setSelectionMode] = useState<'pieces' | 'packages'>('pieces');
  const [selectedPackages, setSelectedPackages] = useState<number[]>([]);

  const [cuttingPieceToPrint, setCuttingPiecesToPrint] = useState<
    ProductionPiece[] | undefined
  >(undefined);

  const [currentStep, setPrintingStep] = useState<number | undefined>(
    undefined,
  );

  const [subdivise, { isLoading }] = useSubdividePartOrderMutation();

  const products = useSelector(selectDirectPartOrderProductsFiltered(filtered));
  const isOrderReLoading = useSelector(selectOrderLoading);
  const hasCutting = useSelector(selectHasCuttingPiece);
  const ordersMap = useSelector(selectOrdersMap);
  const colors = useSelector(selectAllColors);

  const handleSelectedProduct = (productId: number) => {
    const selected = new Set<number>(selectedProducts);
    if (selected.has(productId)) selected.delete(productId);
    else selected.add(productId);
    setSelectedProducts(Array.from(selected));
  };

  const handleSelectedPieces = (pieceId: number) => {
    const selected = new Set<number>(selectedPieces);
    if (selected.has(pieceId)) selected.delete(pieceId);
    else selected.add(pieceId);
    setSelectedPieces(Array.from(selected));
  };

  const handleSelectedPackage = (packageId: number) => {
    const selected = new Set<number>(selectedPackages);
    if (selected.has(packageId)) selected.delete(packageId);
    else selected.add(packageId);
    setSelectedPackages(Array.from(selected));
  };

  function productName(product: ProductOrder): string {
    if (product.name && product.name) return product.name;
    if (product.production.length === 1 && product.production[0].description) {
      return `${product.production[0].quantity} x ${product.production[0].description}`;
    }
    return '-';
  }

  useEffect(() => {
    if (!hasCutting) {
      if (currentStep === undefined) {
        setPrintingStep(1);
        setFiltered(false);
      }
    }
  }, [partOrder, currentStep]);

  useEffect(() => {
    if (selectionMode === 'pieces' && products && (currentStep === 0 || currentStep === undefined)) {
      const selectedPiecesSet = new Set<number>([]);
      products.forEach(product => {
        product.production.forEach((piece: ProductionPiece) => {
          if (piece.station === Workstation.Drawer) {
            selectedPiecesSet.add(piece.id);
          }
        });
      });
      setSelectedPieces(Array.from(selectedPiecesSet));
    }
  }, [partOrder, filtered, selectionMode]);

  useEffect(() => {
    setSelectedPackages([]);
  }, [partOrder]);

  const handleCreateNewPartOrder = () => {
    const data: Record<string, unknown> = { destination: DESTINATION_LEUZE_ID };

    if (selectionMode === 'packages') {
      if (selectedPackages.length > 0) {
        data.packages = selectedPackages.map(id => ({ id }));
      }
    } else {
      if (selectedPieces.length > 0) {
        data.pieces = selectedPieces.map(id => ({ id }));
      }
      if (selectedProducts.length > 0) {
        data.products = selectedProducts;
      }
    }

    subdivise({ id: partOrder.id, data })
      .unwrap()
      .then(() => {
        setPrintingStep(1);
        setSelectedPieces([]);
        setSelectedProducts([]);
        setSelectedPackages([]);
      })
      .catch(error => {
        console.error('Error fetching part orders:', error);
      });
  };

  const getCuttingPrintInfos = (partsOrdersId: number[]) => {
    requestPost(CUTTING_PRINTING_INFO_URL, {
      printPartsOrders: partsOrdersId,
    }).then((data: ProductionPieceJson[]) => {
      const piecesToPrint = data.map((pieceData: ProductionPieceJson) => {
        const piece = createProductionPiece(
          pieceData,
          undefined,
          ordersMap,
          colors,
        );
        if (
          (piece.station === Workstation.Woodwork ||
            piece.station === Workstation.WoodworkSpecial ||
            piece.station === Workstation.Drawer) &&
          piece.color === undefined
        )
          if (
            piece.reference.includes('ALU') ||
            piece.reference.includes('DIBOND')
          ) {
            piece.color = findColor('DIBOND', colors)!;
          } else {
            piece.color = findColor('WOOD', colors)!;
          }
        return piece;
      });
      setCuttingPiecesToPrint(piecesToPrint);
    });
  };

  const cuttingOnAfterPrint = () => {
    setCuttingPiecesToPrint(undefined);
    // dispatch(getAllPartOrders());
  };

  return (
    <Modal
      open={isPrintDirectPartOrderModalOpen}
      onClose={() => {
        setIsPrintDirectPartOrderModal(false);
        setPrintingStep(undefined);
        setFiltered(true);
        setSelectedPackages([]);
        setSelectionMode('pieces');
      }}
      center
      styles={{ modal: { maxWidth: '1200px', width: '90%' }, overlay: { zIndex: 1100 } }}
    >
      <ModalContentStyled className="flex items-center">
        <FiPrinter className="h-8 w-8 mr-4" />
        <TitleModal
          title={`Impression de la commande directe ${partOrder.identification}`}
        />
      </ModalContentStyled>
      {!partOrder.packages ||
        (partOrder.packages?.length === 0 && (
          <div>
            <CircleSpinner size={35} color="#4a5568" />
          </div>
        ))}

      {partOrder.packages &&
        partOrder.packages?.length > 0 &&
        (currentStep === 0 || currentStep === undefined) && (
          <div>
            <div className="flex border-b mb-4">
              <button
                className={`px-4 py-2 ${selectionMode === 'pieces' ? 'border-b-2 border-blue-500 font-semibold' : ''}`}
                onClick={() => { setSelectionMode('pieces'); setSelectedPackages([]); }}
              >
                Par pièces
              </button>
              <button
                className={`px-4 py-2 ${selectionMode === 'packages' ? 'border-b-2 border-blue-500 font-semibold' : ''}`}
                onClick={() => { setSelectionMode('packages'); setSelectedPieces([]); setSelectedProducts([]); }}
              >
                Par colis
              </button>
            </div>

            {selectionMode === 'pieces' && (
              <>
                <div className="border-t py-1 my-1 flex">
                  <div>
                    Sélectionner les pièces qui seront produite{' '}
                    <span className="font-semibold">ultérieurement</span> dans une
                    sous-commande
                  </div>
                  {filtered && (
                    <div
                      className="underline ml-4 cursor-pointer"
                      onClick={() => {
                        setFiltered(false);
                      }}
                    >
                      Voir toute la liste
                    </div>
                  )}
                </div>
                <div className="overflow-y-auto">
                  <div>
                    {products
                      ?.filter(p => !p.drawer)
                      .map((product: ProductOrder) => (
                        <>
                          <div
                            key={`T-${product.id}`}
                            className="w-full flex items-center border cursor-pointer hover:bg-gray-100 mt-2"
                            onClick={() => {
                              handleSelectedProduct(product.id);
                            }}
                          >
                            <div className="w-1/12">
                              {product.production.length === 1 && (
                                <Checkbox
                                  checked={selectedProducts.includes(product.id)}
                                  color="primary"
                                  name={`${product.id}`}
                                />
                              )}
                            </div>
                            <div className="w-11/12">
                              <div className="flex items-center justify-between text-xl ">
                                {productName(product)}
                                {product.image && (
                                  <div className="mr-20 w-12 h-12">
                                    <Img src={product.image} alt={`preview `} />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          {product.production.length > 1 &&
                            _.orderBy(
                              product.production.filter(piece => {
                                if (filtered) {
                                  return (
                                    piece.cutting ||
                                    piece.station === Workstation.Drawer ||
                                    piece.isFragile
                                  );
                                } else return true;
                              }),
                              ['sorting', 'reference'],
                            ).map((piece: ProductionPiece) => (
                              <div
                                key={`P-${piece.id}`}
                                className={`flex border-b border-l border-r items-start min-h-8 py-2 justify-start text-base cursor-pointer hover:bg-gray-100 ${
                                  selectedPieces.includes(piece.id)
                                    ? 'bg-gray-200'
                                    : ''
                                }`}
                                onClick={() => {
                                  handleSelectedPieces(piece.id);
                                }}
                              >
                                <div className="w-1/6">
                                  <div className="flex items-center">
                                    <Checkbox
                                      checked={selectedPieces.includes(piece.id)}
                                      color="primary"
                                      name={`${piece.id}`}
                                    />
                                    {piece.quantity} x
                                  </div>
                                </div>
                                <div className="w-2/12 flex justify-start items-center">
                                  {piece.cutting && (
                                    <GiCircularSawblade className="h-5 w-5 mr-2 text-gray-700" />
                                  )}
                                  {piece.station === Workstation.Drawer && (
                                    <RiArchiveDrawerLine className="h-5 w-5 mr-2 text-gray-700" />
                                  )}
                                  {piece.isFragile && (
                                    <FaWineGlass className="h-5 w-5 mr-2 text-gray-700" />
                                  )}
                                  {piece.reference}
                                </div>
                                <div
                                  className="w-9/12 flex justify-start [&_b]:whitespace-nowrap"
                                  dangerouslySetInnerHTML={{
                                    __html: String(piece.description || '')
                                      .replace(/<b>/g, '&nbsp;<b>')
                                      .replace(/<\/b>/g, '</b>&nbsp;'),
                                  }}
                                />
                              </div>
                            ))}
                        </>
                      ))}
                  </div>
                </div>
              </>
            )}

            {selectionMode === 'packages' && (
              <div className="overflow-y-auto">
                {partOrder.packages?.map((pkg: PackageOrder) => (
                  <div
                    key={`PKG-${pkg.id}`}
                    className={`w-full flex items-center border cursor-pointer hover:bg-gray-100 mt-2 ${
                      selectedPackages.includes(pkg.id) ? 'bg-gray-200' : ''
                    }`}
                    onClick={() => handleSelectedPackage(pkg.id)}
                  >
                    <div className="w-1/12">
                      <Checkbox checked={selectedPackages.includes(pkg.id)} color="primary" />
                    </div>
                    <div className="w-11/12 text-xl">
                      {pkg.name ?? pkg.description ?? `Colis #${pkg.packageNumber}`}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="w-full flex justify-end items-center">
              <div className="mx-4">
                {selectionMode === 'pieces' && (
                  <>
                    {selectedPieces.length + selectedProducts.length === 0 && (
                      <div>
                        Le client va retirer la totalité de sa commande
                        <span className="font-semibold ml-1">aujourd'hui</span>
                      </div>
                    )}
                    {selectedPieces.length + selectedProducts.length === 1 && (
                      <div>
                        <span className="font-semibold">1 pièce</span> ne sera pas
                        produite aujourd'hui et devra être enlevée{' '}
                        <span className="font-semibold ml-1">dans le future</span>
                      </div>
                    )}
                    {selectedPieces.length + selectedProducts.length > 1 && (
                      <div>
                        <span className="font-semibold">
                          {selectedPieces.length + selectedProducts.length} pièces
                        </span>{' '}
                        ne seront pas produite aujourd'hui et devront être enlevées{' '}
                        <span className="font-semibold ml-1">dans le future</span>
                      </div>
                    )}
                  </>
                )}
                {selectionMode === 'packages' && (
                  <>
                    {selectedPackages.length === 0 && (
                      <div>
                        Le client va retirer la totalité de sa commande
                        <span className="font-semibold ml-1">aujourd'hui</span>
                      </div>
                    )}
                    {selectedPackages.length === 1 && (
                      <div>
                        <span className="font-semibold">1 colis</span> ne sera pas
                        produit aujourd'hui et devra être enlevé{' '}
                        <span className="font-semibold ml-1">dans le future</span>
                      </div>
                    )}
                    {selectedPackages.length > 1 && (
                      <div>
                        <span className="font-semibold">
                          {selectedPackages.length} colis
                        </span>{' '}
                        ne seront pas produits aujourd'hui et devront être enlevés{' '}
                        <span className="font-semibold ml-1">dans le future</span>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="my-2">
                <Button
                  onClick={() => {
                    const hasSelection = selectionMode === 'packages'
                      ? selectedPackages.length > 0
                      : selectedPieces.length + selectedProducts.length > 0;
                    if (hasSelection) {
                      handleCreateNewPartOrder();
                    } else {
                      setPrintingStep(1);
                      setSelectedPieces([]);
                      setSelectedProducts([]);
                      setSelectedPackages([]);
                    }
                  }}
                  contentText="Suivant"
                  loading={isLoading}
                />
              </div>
            </div>
          </div>
        )}
      {partOrder.packages &&
        partOrder.packages?.length > 0 &&
        currentStep === 1 && (
          <div>
            <div className="flex items-center justify-center">
              {isOrderReLoading ? (
                <div className="my-12">
                  <CircleSpinner size={35} color="#4a5568" />
                </div>
              ) : (
                <div>
                  <div className="flex flex-col my-12">
                    <div className="flex items-center my-2 text-xl">
                      Imprimer les feuilles de décomposition:{' '}
                      <PrintOrder order={partOrder} />
                    </div>

                    {hasCutting && (
                      <div>
                        <div className="border-t border-gray-200 my-5" />
                        <div className="flex items-center my-2 text-xl">
                          Imprimer les feuilles de découpes:{' '}
                          <button
                            className="mt-2 ml-2"
                            data-tip="Imprimer"
                            data-for="infos"
                            onClick={() => {
                              getCuttingPrintInfos([partOrder.id]);
                            }}
                          >
                            <FiPrinter className="h-6 w-6 ml-2" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    className="border py-1 px-2 border-gray-700"
                    onClick={() => {
                      setPrintingStep(0);
                    }}
                  >
                    Créer une sous-commande
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

      {cuttingPieceToPrint && (
        <CuttingToPrint
          pieces={cuttingPieceToPrint}
          onAfterPrint={cuttingOnAfterPrint}
        />
      )}
    </Modal>
  );
}

export const ModalContentStyled = styled.div`
  width: 1200px;
`;
